/**
 * What happened.
 *
 * One component, used at two sizes. On the records screen it is the whole log
 * with its filters; inside an aircraft drawer or a student record it is the
 * last few lines about that one thing, with a way through to the rest. Both
 * read the same endpoint, and the server decides what the person may see —
 * there is no filtering here that the API does not also apply.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useQuery } from '../api.js';
import { useLive } from '../live.js';
import { Menu, MenuItem, MenuSeparator, SearchField, Segmented } from '../controls.js';
import { ErrorNote, Spinner } from '../ui.js';
import { ChevronDown } from '../icons.js';
import type { HistoryEvent, HistoryGroup, HistoryPage } from '../../../shared/types.js';

interface Page extends HistoryPage {
  availableGroups: HistoryGroup[];
}

/** How far back the log is read. Anything is a lot of rows; a month is a shift. */
const RANGES: { value: string; label: string; days: number }[] = [
  { value: '7', label: '7 days', days: 7 },
  { value: '30', label: '30 days', days: 30 },
  { value: '365', label: 'Year', days: 365 },
  { value: '0', label: 'All', days: 0 },
];

/* ── Day headings ─────────────────────────────────────────────────────── */

function dayKey(at: number): string {
  return new Date(at).toDateString();
}

function dayLabel(at: number): string {
  const key = dayKey(at);
  const today = new Date();
  if (key === today.toDateString()) return 'Today';
  const yesterday = new Date(today.getTime() - 86400000);
  if (key === yesterday.toDateString()) return 'Yesterday';
  const d = new Date(at);
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString('en-US', {
    weekday: 'long', day: 'numeric', month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

const clock = (at: number) =>
  new Date(at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

/** Group consecutive events under the day they happened. */
function byDay(events: HistoryEvent[]): { day: string; label: string; events: HistoryEvent[] }[] {
  const out: { day: string; label: string; events: HistoryEvent[] }[] = [];
  for (const e of events) {
    const day = dayKey(e.at);
    if (out[out.length - 1]?.day !== day) out.push({ day, label: dayLabel(e.at), events: [] });
    out[out.length - 1].events.push(e);
  }
  return out;
}

/* ── One line ─────────────────────────────────────────────────────────── */

function EventRow({ event, onOpen }: { event: HistoryEvent; onOpen?: (href: string) => void }): JSX.Element {
  const body = (
    <>
      <span className="hist__time">{clock(event.at)}</span>
      <span className={`hist__dot sev-${event.severity}`} />
      <span className="hist__what">
        <span className="hist__line">
          {event.actorName}
          {' '}
          <span className="hist__verb">
            {event.label.charAt(0).toLowerCase() + event.label.slice(1)}
          </span>
          {event.entityLabel && <> <span className="hist__subject">{event.entityLabel}</span></>}
        </span>
        {event.detail && <span className="hist__detail">{event.detail}</span>}
      </span>
      <span className="hist__group">{event.group}</span>
    </>
  );

  if (!event.href || !onOpen) return <div className="hist__row">{body}</div>;
  return (
    <button type="button" className="hist__row hist__row--link" onClick={() => onOpen(event.href)}>
      {body}
    </button>
  );
}

/* ── The feed ─────────────────────────────────────────────────────────── */

export interface HistoryProps {
  /** Narrow to one thing's own history. Both are needed, or neither. */
  entity?: string;
  entityId?: number;
  /** Embedded: a few lines, no filter bar. */
  compact?: boolean;
  /** How many lines a compact feed shows before it offers the rest. */
  limit?: number;
  /** Shown when there is nothing to show. */
  emptyNote?: string;
}

export function History({ entity, entityId, compact, limit, emptyNote }: HistoryProps): JSX.Element {
  const navigate = useNavigate();
  const [group, setGroup] = useState<HistoryGroup | 'all'>('all');
  const [actorId, setActorId] = useState<number | 'all'>('all');
  const [range, setRange] = useState(compact ? '0' : '30');
  const [q, setQ] = useState('');
  /** Pages loaded past the first, kept separately so the first can refresh. */
  const [more, setMore] = useState<HistoryEvent[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const pageSize = compact ? limit ?? 6 : 60;

  const path = useMemo(() => {
    const p = new URLSearchParams();
    if (entity) p.set('entity', entity);
    if (entityId != null) p.set('entityId', String(entityId));
    if (group !== 'all') p.set('group', group);
    if (actorId !== 'all') p.set('actorId', String(actorId));
    if (q.trim()) p.set('q', q.trim());
    const days = RANGES.find((r) => r.value === range)?.days ?? 0;
    if (days) p.set('days', String(days));
    p.set('limit', String(pageSize));
    return `/history?${p.toString()}`;
  }, [entity, entityId, group, actorId, q, range, pageSize]);

  const { data, error, loading, reload } = useQuery<Page>(path);

  // Any filter change starts the list over, or the older pages below would
  // still be answering the previous question.
  useEffect(() => { setMore([]); setCursor(null); }, [path]);
  useLive('records', reload);
  useLive('schedule', reload);

  const loadMore = useCallback(async () => {
    const from = cursor ?? data?.nextCursor;
    if (from == null) return;
    setLoadingMore(true);
    try {
      const next = await api.get<Page>(`${path}&before=${from}`);
      setMore((m) => [...m, ...next.events]);
      setCursor(next.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, data?.nextCursor, path]);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const events = [...data.events, ...more];
  const hasMore = (cursor ?? data.nextCursor) != null;
  const days = byDay(events);
  const open = (href: string) => navigate(href);

  /* Compact: the last few lines, and a way to the rest. */
  if (compact) {
    return (
      <div className="hist hist--compact">
        {events.length === 0 && (
          <div className="empty">{emptyNote ?? 'Nothing recorded yet.'}</div>
        )}
        {events.slice(0, pageSize).map((e) => (
          <div className="hist__row hist__row--tight" key={e.id}>
            <span className="hist__when">
              {/* The year appears only when it is not this one. A compact feed
                  reaches back years, and "Mar 30" alone does not say which. */}
              {new Date(e.at).toLocaleDateString('en-US', {
                day: 'numeric', month: 'short',
                ...(new Date(e.at).getFullYear() === new Date().getFullYear()
                  ? {} : { year: '2-digit' }),
              })}
            </span>
            <span className={`hist__dot sev-${e.severity}`} />
            <span className="hist__what">
              <span className="hist__line">
                {e.actorName} <span className="hist__verb">
                  {e.label.charAt(0).toLowerCase() + e.label.slice(1)}
                </span>
              </span>
              {e.detail && <span className="hist__detail">{e.detail}</span>}
            </span>
          </div>
        ))}
        {data.total > events.length && (
          <button
            className="btn btn--link"
            style={{ margin: '6px 0 2px' }}
            onClick={() => navigate(`/records?tab=history&entity=${entity}&entityId=${entityId}`)}
          >
            All {data.total} events
          </button>
        )}
      </div>
    );
  }

  /* Full: the log, with the filters that make it answerable. */
  const actorName = data.actors.find((a) => a.id === actorId)?.name;

  return (
    <div className="hist">
      <div className="hist__bar">
        <Segmented
          value={range}
          onChange={setRange}
          label="How far back"
          options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
        />

        <Menu
          align="left"
          trigger={(p) => (
            <button {...p} className={group === 'all' ? 'chip' : 'chip chip--on'}>
              {group === 'all' ? 'All sections' : group}
              <ChevronDown style={{ width: 12, height: 12 }} />
            </button>
          )}
        >
          <div className="menu__label">Section</div>
          <MenuItem onClick={() => setGroup('all')}>All sections</MenuItem>
          <MenuSeparator />
          {data.groups.map((g) => (
            <MenuItem key={g.group} onClick={() => setGroup(g.group)}>
              {g.group} · {g.count}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          align="left"
          trigger={(p) => (
            <button {...p} className={actorId === 'all' ? 'chip' : 'chip chip--on'}>
              {actorId === 'all' ? 'Anyone' : actorName ?? 'Someone'}
              <ChevronDown style={{ width: 12, height: 12 }} />
            </button>
          )}
        >
          <div className="menu__label">Who</div>
          <MenuItem onClick={() => setActorId('all')}>Anyone</MenuItem>
          <MenuSeparator />
          {data.actors.slice(0, 20).map((a) => (
            <MenuItem key={a.id ?? 'system'} onClick={() => setActorId(a.id)}>
              {a.name} · {a.count}
            </MenuItem>
          ))}
        </Menu>

        <span style={{ flex: 1, minWidth: 140 }}>
          <SearchField
            value={q}
            onChange={setQ}
            label="Search the record"
            placeholder="Tail number, name, invoice…"
          />
        </span>
      </div>

      <div className="hist__count">
        {data.total.toLocaleString()} event{data.total === 1 ? '' : 's'}
        {q.trim() && ' · search matches the lines loaded below'}
        {loading && ' · refreshing…'}
      </div>

      {events.length === 0 && (
        <div className="empty">
          {emptyNote ?? 'Nothing was recorded in this range.'}
        </div>
      )}

      {days.map((d) => (
        <section className="hist__day" key={d.day}>
          <h3 className="hist__dayhead">{d.label}</h3>
          {d.events.map((e) => <EventRow key={e.id} event={e} onOpen={open} />)}
        </section>
      ))}

      {hasMore && (
        <button className="btn" style={{ margin: '10px auto', display: 'block' }}
          disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? 'Loading…' : 'Older'}
        </button>
      )}
    </div>
  );
}
